import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
  Slide,
  Stack,
  Box,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import { forwardRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import axios from 'axios'
import { REST_API_END_POINT } from 'src/constants/Defaultvalues'

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function AddToStockPopup({
  open,
  handleClose,
  projectId,
  onConfirm
}) {
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [tasks, setTasks] = useState([])
  const [productSelections, setProductSelections] = useState({})
  const [fetchingTasks, setFetchingTasks] = useState(false)
  const [finishedProducts, setFinishedProducts] = useState({})
  const [fetchingFinishedProducts, setFetchingFinishedProducts] = useState({})
  const [selectedFinishedProducts, setSelectedFinishedProducts] = useState({})
  const [tasksWithFinishedProducts, setTasksWithFinishedProducts] = useState({})

  // Fetch project tasks when popup opens
  useEffect(() => {
    if (open && projectId) {
      fetchProjectTasks()
    } else if (!open) {
      // Reset state when popup closes
      setTasks([])
      setProductSelections({})
      setFinishedProducts({})
      setSelectedFinishedProducts({})
      setFetchingFinishedProducts({})
      setTasksWithFinishedProducts({})
    }
  }, [open, projectId])

  const fetchProjectTasks = async () => {
    setFetchingTasks(true)
    try {
      const response = await axios.post(`${REST_API_END_POINT}project/get-project-by-id`, {
        id: { value: projectId },
        userId: JSON.parse(localStorage.getItem('user'))?.user_id,
        userType: JSON.parse(localStorage.getItem('user'))?.user_type,
        hasPermission: true,
      })

      if (response.data.status === 1) {
        // Filter tasks based on requirements: product_id not null, inhouse != 1, isTrafficCatalog = 0
        const filteredTasks = response.data.projectTask?.filter(task => 
          task.product_id && 
          ((task.inhouse === 1 && task.isTrafficCatalog === 1) || task.inhouse !== 1)
        ) || []

        setTasks(filteredTasks)
        
        // Initialize product selections with default value "add to new stock"
        const initialSelections = {}
        filteredTasks.forEach(task => {
          initialSelections[task.ID] = 'add_to_new_stock'
        })
        setProductSelections(initialSelections)
        
        // Pre-fetch finished products for all tasks to determine which have finished products available
        const tasksWithFinishedProductsMap = {}
        const finishedProductsMap = {}
        
        // Fetch finished products for all tasks in parallel
        const finishedProductPromises = filteredTasks.map(async (task) => {
          if (task.product_id) {
            try {
              const response = await axios.post(`${REST_API_END_POINT}product/get-finished-products`, {
                editId: task.product_id
              })
              
              if (response.data.status === 1) {
                // Filter products to only show those with source = 0
                const filteredProducts = (response.data.results || []).filter(product => product.source === 0)
                finishedProductsMap[task.ID] = filteredProducts
                tasksWithFinishedProductsMap[task.ID] = filteredProducts.length > 0
              } else {
                finishedProductsMap[task.ID] = []
                tasksWithFinishedProductsMap[task.ID] = false
              }
            } catch (error) {
              console.error(`Error fetching finished products for task ${task.ID}:`, error)
              finishedProductsMap[task.ID] = []
              tasksWithFinishedProductsMap[task.ID] = false
            }
          } else {
            finishedProductsMap[task.ID] = []
            tasksWithFinishedProductsMap[task.ID] = false
          }
        })
        
        // Wait for all finished product fetches to complete
        await Promise.all(finishedProductPromises)
        
        setFinishedProducts(finishedProductsMap)
        setTasksWithFinishedProducts(tasksWithFinishedProductsMap)
      }
    } catch (error) {
      console.error('Error fetching project tasks:', error)
      enqueueSnackbar('Failed to fetch project tasks', { variant: 'error' })
    } finally {
      setFetchingTasks(false)
    }
  }


  const handleSelectionChange = (taskId, value) => {
    setProductSelections(prev => ({
      ...prev,
      [taskId]: value
    }))

    // Clear finished product selection when switching away from existing stock
    if (value !== 'add_to_existing_stock') {
      setSelectedFinishedProducts(prev => {
        const newState = { ...prev }
        delete newState[taskId]
        return newState
      })
    }
  }

  const handleFinishedProductChange = (taskId, finishedProductId) => {
    setSelectedFinishedProducts(prev => ({
      ...prev,
      [taskId]: finishedProductId
    }))
  }

  const handleConfirm = () => {
    if (Object.keys(productSelections).length === 0) {
      enqueueSnackbar('No products to add to stock', { variant: 'warning' })
      return
    }

    // Check if any "Add to existing stock" selections don't have a finished product selected
    const missingFinishedProductSelections = Object.keys(productSelections).filter(taskId => 
      productSelections[taskId] === 'add_to_existing_stock' && !selectedFinishedProducts[taskId]
    )

    if (missingFinishedProductSelections.length > 0) {
      enqueueSnackbar('Please select finished products for all "Add to existing stock" items', { variant: 'warning' })
      return
    }

    setLoading(true)
    
    // Prepare data for confirmation
    const stockData = tasks.map(task => ({
      taskId: task.ID,
      productId: task.product_id,
      productName: task.productName || task.name,
      selection: productSelections[task.ID],
      finishedProductId: selectedFinishedProducts[task.ID] || null,
      finishedProduct: finishedProducts[task.ID]?.find(fp => fp.id === selectedFinishedProducts[task.ID]) || null
    }))

    // Call the parent's confirmation handler
    if (onConfirm) {
      onConfirm(stockData)
    }
    
    setLoading(false)
    handleClose()
  }

  return (
    <Dialog
      aria-describedby="add-to-stock-dialog-description"
      TransitionComponent={Transition}
      transitionDuration={500}
      sx={{ minWidth: { xs: 0, sm: 600 } }}
      open={open}
      onClose={handleClose}
      onBackdropClick={handleClose}
    >
      <DialogTitle>Add Products to Stock</DialogTitle>
      <DialogContent sx={{ minWidth: 270, mt: -2 }}>
        <DialogContentText id="add-to-stock-dialog-description">
          Select how you want to add each product to stock:
        </DialogContentText>
        
        {fetchingTasks ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading products...
            </Typography>
          </Box>
        ) : tasks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No products found matching the criteria.
          </Typography>
        ) : (
          <List sx={{ mt: 2 }}>
            {tasks.map((task, index) => (
              <Box key={task.ID}>
                <ListItem sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                      <Tooltip title={task.productName || task.name} arrow placement="top">
                        <ListItemText
                          primary={task.productName || task.name}
                          // secondary={`Task ID: ${task.ID}`}
                          sx={{ 
                            flex: 1,
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mr: 2,
                            '& .MuiListItemText-primary': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      </Tooltip>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={productSelections[task.ID] || 'add_to_new_stock'}
                        onChange={(e) => handleSelectionChange(task.ID, e.target.value)}
                        displayEmpty
                      >
                        {tasksWithFinishedProducts[task.ID] && (
                          <MenuItem value="add_to_existing_stock">
                            Add to existing stock
                          </MenuItem>
                        )}
                        <MenuItem value="add_to_new_stock">
                          Add as new stock
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  {/* Finished Products Selection - Only show when "Add to existing stock" is selected */}
                  {productSelections[task.ID] === 'add_to_existing_stock' && tasksWithFinishedProducts[task.ID] && (
                    <Box sx={{ width: '100%', ml: 2, mb: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 300 }}>
                        <Select
                          value={selectedFinishedProducts[task.ID] || ''}
                          onChange={(e) => handleFinishedProductChange(task.ID, e.target.value)}
                          displayEmpty
                          placeholder="Select finished product"
                        >
                          <MenuItem value="" disabled>
                            Select finished product
                          </MenuItem>
                          {finishedProducts[task.ID].map((fp) => (
                            <MenuItem key={fp.id} value={fp.id}>
                              {fp.name || `Product #${fp.id}`} 
                              {fp.quantity && ` (Qty: ${fp.quantity})`}
                              {fp.unit_cost && ` - $${fp.unit_cost}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </ListItem>
                {index < tasks.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
      
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        px={3}
        mb={2.5}
        mt={-1}
      >
        <Button
          size="medium"
          onClick={() => {
            handleClose()
            enqueueSnackbar('Operation was cancelled', { variant: 'error' })
          }}
          sx={{
            background: '#BB2525',
            boxShadow: `0 8px 16px 0 ${alpha('#BB2525', 0.24)}`,
            '&:hover': {
              background: '#FF8F8F',
              boxShadow: `0 8px 16px 0 ${alpha('#FF8F8F', 0.24)}`,
            },
          }}
        >
          <Typography
            sx={{ color: theme.palette.common.white }}
            variant="overline"
          >
            Cancel
          </Typography>
        </Button>
        
        <Button
          size="medium"
          variant="contained"
          onClick={handleConfirm}
          sx={{ color: theme.palette.primary.main }}
          disabled={loading || fetchingTasks || tasks.length === 0}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: theme.palette.common.white }} />
          ) : (
            <Typography
              sx={{ color: theme.palette.common.white }}
              variant="overline"
            >
              Confirm
            </Typography>
          )}
        </Button>
      </Stack>
    </Dialog>
  )
}
